/** Connected yDoc */

import { logger } from '@tunasong/models'
import { YjsEditor } from '@tunasong/sync-lib'
import { useEffect } from 'react'
import { Editor } from '../slate-typescript.js'
import type { TunaEditor } from '../plugin-types.js'

/** Connect YDoc to the editor */
export const useYDocConnect = (editor: TunaEditor | null) => {
  // Connect editor in useEffect to comply with concurrent mode requirements.
  useEffect(() => {
    if (!editor) {
      return
    }
    if (YjsEditor.isYjsEditor(editor) === false) {
      throw new Error('useYDocConnected: editor must be a YjsEditor')
    }
    const yjsEditor = editor as unknown as YjsEditor
    YjsEditor.connect(yjsEditor)

    /**
     * Shape-correction normalize. `connect()` already force-normalizes, but a
     * normalizer that reads an ancestor or sibling can leave work that only a
     * second pass finds, because Slate does not re-dirty children when a
     * parent's props change.
     *
     * GH #1339: these ops must run under the editor's local origin so slate-yjs
     * stores them and writes them to the YDoc. They once ran under a separate
     * origin to keep them off the wire (GH #1161), which left them in
     * editor.children but not in the YDoc. slate-yjs maps Slate paths onto the
     * Y tree by position, so the two trees disagreed for the rest of the
     * session. The next local op was replayed against a Y tree of a different
     * shape and threw "Path doesn't match yText" (yTarget spans multiple
     * nodes / cannot descent into non-yText), and remote edits landed at the
     * wrong offsets. The #1161 flood comes from `connect()`'s own normalize,
     * which already broadcasts under the local origin. This pass only adds the
     * ops a single pass misses.
     */
    try {
      Editor.normalize(yjsEditor as unknown as TunaEditor, { force: true })
    } catch (e) {
      /**
       * GH #1339 / TUNASONG-VX: on a YDoc whose structure has desynced from
       * the Slate mapping (the #1339 family), this heal-normalize can itself
       * throw from inside slate-yjs ("Path doesn't match yText", "cannot
       * descent into non-yText"). Letting it bubble has two consequences: the
       * mount crashes into the nearest ErrorBoundary (white-screening the
       * song), and the effect never returns its cleanup — leaking the YJS
       * connection. Rendering the document un-healed is strictly better:
       * every normalize op that already applied left a valid Slate doc, so
       * partial normalization is safe to render, and normalization re-runs on
       * the next change anyway. Log as an error so the desync telemetry
       * (VM/VX) is preserved without the crash.
       */
      logger.error('useYDocConnect: shape-correction normalize failed; rendering un-healed', e)
    }

    return () => {
      /**
       * TUNASONG-W3: `disconnect()` flushes pending local changes into the
       * YDoc, and on a desynced doc (the #1339 family) slate-yjs can throw
       * "Cannot insert text into non-text node" from inside
       * `flushLocalChanges`. A throwing effect cleanup crashes the app into the
       * nearest ErrorBoundary on unmount/teardown — strictly worse than
       * dropping the un-flushable (already structurally-invalid) local ops.
       * Mirror the connect-side guard above: swallow + log so teardown is safe
       * and the desync telemetry (VM/VX/W3) is preserved.
       */
      try {
        YjsEditor.disconnect(yjsEditor)
      } catch (e) {
        logger.error('useYDocConnect: disconnect/flush failed on teardown; releasing un-healed', e)
      }
    }
  }, [editor])
}
